import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Flame, Wallet, Users, Trophy, ChevronRight, Check, X, Clock, 
  Zap, Shield, Star, AlertCircle, Settings, Target,
  Twitter, MessageCircle, Gift, Sparkles, Award, ExternalLink,
  CheckCircle2, ChevronLeft, Gem, CircleDollarSign, ChevronDown,
  Calendar, Activity, Eye, AlertTriangle, ChevronUp, Timer,
  Repeat, ArrowUpRight, Copy, Plus
} from "lucide-react";
import { useApp } from "../contexts/AppContext";
import { DEMO } from "../constants/mockData";
import { DISCORD_INVITE, UEX_REFERRAL } from "../constants";

const ProfilePage = () => {
  const { user, setWalletModalOpen, setUser } = useApp();
  const [activeTab, setActiveTab] = useState("team");
  const [activityFilter, setActivityFilter] = useState("all");
  const [claiming, setClaiming] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showTwitterModal, setShowTwitterModal] = useState(false);
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [connectingTwitter, setConnectingTwitter] = useState(false);
  const [connectingDiscord, setConnectingDiscord] = useState(false);

  // Load connection status from localStorage
  const [twitterConnected, setTwitterConnected] = useState(() => localStorage.getItem('burnz_twitter_connected') === 'true');
  const [discordConnected, setDiscordConnected] = useState(() => localStorage.getItem('burnz_discord_connected') === 'true');
  const [twitterHandle, setTwitterHandle] = useState(() => localStorage.getItem('burnz_twitter_handle') || '');
  const [discordHandle, setDiscordHandle] = useState(() => localStorage.getItem('burnz_discord_handle') || '');

  const handleConnectTwitter = async () => {
    setConnectingTwitter(true);
    await new Promise(r => setTimeout(r, 1000));
    setTwitterConnected(true);
    setTwitterHandle('@user_handle');
    localStorage.setItem('burnz_twitter_connected', 'true');
    localStorage.setItem('burnz_twitter_handle', '@user_handle');
    setConnectingTwitter(false);
    setShowTwitterModal(false);
  };

  const handleDisconnectTwitter = () => {
    setTwitterConnected(false);
    setTwitterHandle('');
    localStorage.removeItem('burnz_twitter_connected');
    localStorage.removeItem('burnz_twitter_handle');
  };

  const handleConnectDiscord = async () => {
    setConnectingDiscord(true);
    await new Promise(r => setTimeout(r, 1000));
    setDiscordConnected(true);
    setDiscordHandle('User#1234');
    localStorage.setItem('burnz_discord_connected', 'true');
    localStorage.setItem('burnz_discord_handle', 'User#1234');
    setConnectingDiscord(false);
    setShowDiscordModal(false);
  };

  const handleDisconnectDiscord = () => {
    setDiscordConnected(false);
    setDiscordHandle('');
    localStorage.removeItem('burnz_discord_connected');
    localStorage.removeItem('burnz_discord_handle');
  };

  const handleClaim = async () => { setClaiming(true); await new Promise(r => setTimeout(r, 1500)); setClaiming(false); };

  // Team Switching State
  const [showTeamSwitchModal, setShowTeamSwitchModal] = useState(false);
  const [switchingTeam, setSwitchingTeam] = useState(false);
  const [selectedNewTeam, setSelectedNewTeam] = useState(null);
  
  // Load team data from localStorage
  const [currentTeam, setCurrentTeam] = useState(() => {
    const stored = localStorage.getItem('burnz_user_team');
    if (stored) {
      try { return JSON.parse(stored); } catch { return null; }
    }
    // Default team for demo
    return {
      id: 'team_phoenix',
      name: 'Phoenix Rising',
      emoji: '🔥',
      theme: 'fire',
      joinedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(), // 18 days ago
      rank: 2,
      totalXp: 2450000,
      myContribution: 12400,
      myBurnContribution: 840,
      teamBurnTotal: 45200,
      activeCampaigns: 0,
    };
  });

  const [teamHistory, setTeamHistory] = useState(() => {
    const stored = localStorage.getItem('burnz_team_history');
    if (stored) {
      try { return JSON.parse(stored); } catch { return []; }
    }
    // Demo history
    return [
      {
        id: 'team_phoenix',
        name: 'Phoenix Rising',
        emoji: '🔥',
        joinedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
        leftAt: null,
        daysInTeam: 18,
        xpContributed: 12400,
        burnzBurned: 840,
        achievementsEarned: ['first_contribution', 'weekly_active'],
        isCurrent: true,
      },
      {
        id: 'team_diamond',
        name: 'Diamond Hands',
        emoji: '💎',
        joinedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        leftAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
        daysInTeam: 27,
        xpContributed: 8600,
        burnzBurned: 520,
        achievementsEarned: ['team_milestone'],
        isCurrent: false,
      }
    ];
  });

  const [switchCount, setSwitchCount] = useState(() => {
    const stored = localStorage.getItem('burnz_switch_count');
    return stored ? parseInt(stored) : 1; // Already switched once in demo
  });

  const [lastSwitchDate, setLastSwitchDate] = useState(() => {
    const stored = localStorage.getItem('burnz_last_switch');
    return stored || new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString();
  });

  // Available teams for switching
  const availableTeams = [
    { id: 'team_phoenix', name: 'Phoenix Rising', emoji: '🔥', rank: 2, totalXp: 2450000, activeMemberCount: 1247, momentum: '+12%', theme: 'from-orange-500 to-red-600' },
    { id: 'team_diamond', name: 'Diamond Hands', emoji: '💎', rank: 1, totalXp: 2890000, activeMemberCount: 892, momentum: '+8%', theme: 'from-cyan-400 to-blue-600' },
    { id: 'team_moon', name: 'Moon Walkers', emoji: '🌙', rank: 3, totalXp: 1560000, activeMemberCount: 756, momentum: '+5%', theme: 'from-purple-500 to-indigo-600' },
    { id: 'team_gold', name: 'Golden Phoenix', emoji: '✨', rank: 4, totalXp: 980000, activeMemberCount: 445, momentum: '+18%', theme: 'from-yellow-400 to-amber-600' },
    { id: 'team_emerald', name: 'Emerald Legion', emoji: '💚', rank: 5, totalXp: 720000, activeMemberCount: 312, momentum: '+22%', theme: 'from-green-400 to-emerald-600' },
  ];

  // Calculate days since joining current team - memoized to prevent recalculation on every render
  const [now] = useState(() => Date.now());
  const daysSinceJoin = currentTeam ? Math.floor((now - new Date(currentTeam.joinedAt).getTime()) / (1000 * 60 * 60 * 24)) : 0;
  
  // Calculate days since last switch
  const daysSinceSwitch = Math.floor((now - new Date(lastSwitchDate).getTime()) / (1000 * 60 * 60 * 24));
  
  // Cooldown check: 7 days after joining, 14 days after switching
  const joinCooldownDays = 7;
  const switchCooldownDays = 14;
  const cooldownRemaining = switchCount === 0 
    ? Math.max(0, joinCooldownDays - daysSinceJoin)
    : Math.max(0, switchCooldownDays - daysSinceSwitch);
  const canSwitch = cooldownRemaining === 0 && currentTeam?.activeCampaigns === 0;

  // Switch cost calculation (tier-based)
  const getSwitchCost = () => {
    if (switchCount === 0) return 0; // First switch free
    const userLevel = user?.level || 4;
    if (userLevel < 25) return 250;
    if (userLevel < 50) return 500;
    if (userLevel < 75) return 750;
    return 1000;
  };

  // Team history stats
  const totalTeamsJoined = teamHistory.length;
  const totalDaysInTeams = teamHistory.reduce((sum, t) => sum + (t.daysInTeam || 0), 0);
  const totalXpContributed = teamHistory.reduce((sum, t) => sum + (t.xpContributed || 0), 0);
  const totalBurnzBurned = teamHistory.reduce((sum, t) => sum + (t.burnzBurned || 0), 0);

  const handleTeamSwitch = async () => {
    if (!selectedNewTeam || !canSwitch) return;
    
    const cost = getSwitchCost();
    const userBurnz = user?.burnz_tokens || DEMO.user.burnz_tokens;
    
    if (cost > 0 && userBurnz < cost) {
      alert(`Insufficient BURNZ. You need ${cost} BURNZ to switch teams.`);
      return;
    }

    setSwitchingTeam(true);
    await new Promise(r => setTimeout(r, 1500));

    // Update team history - mark current as left
    const updatedHistory = teamHistory.map(t => 
      t.isCurrent ? { ...t, isCurrent: false, leftAt: new Date().toISOString() } : t
    );

    // Add new team to history
    const newTeamData = availableTeams.find(t => t.id === selectedNewTeam);
    updatedHistory.push({
      id: newTeamData.id,
      name: newTeamData.name,
      emoji: newTeamData.emoji,
      joinedAt: new Date().toISOString(),
      leftAt: null,
      daysInTeam: 0,
      xpContributed: 0,
      burnzBurned: 0,
      achievementsEarned: [],
      isCurrent: true,
    });

    setTeamHistory(updatedHistory);
    localStorage.setItem('burnz_team_history', JSON.stringify(updatedHistory));

    // Set new current team (with reset contributions!)
    const newCurrentTeam = {
      id: newTeamData.id,
      name: newTeamData.name,
      emoji: newTeamData.emoji,
      theme: newTeamData.theme,
      joinedAt: new Date().toISOString(),
      rank: newTeamData.rank,
      totalXp: newTeamData.totalXp,
      myContribution: 0, // RESET!
      myBurnContribution: 0, // RESET!
      teamBurnTotal: 0,
      activeCampaigns: 0,
    };
    setCurrentTeam(newCurrentTeam);
    localStorage.setItem('burnz_user_team', JSON.stringify(newCurrentTeam));

    // Update switch count and date
    const newSwitchCount = switchCount + 1;
    setSwitchCount(newSwitchCount);
    localStorage.setItem('burnz_switch_count', newSwitchCount.toString());
    
    const now = new Date().toISOString();
    setLastSwitchDate(now);
    localStorage.setItem('burnz_last_switch', now);

    setSwitchingTeam(false);
    setShowTeamSwitchModal(false);
    setSelectedNewTeam(null);
    
    alert(`Successfully joined ${newTeamData.name}! Your team contributions have been reset.`);
  };

  const tabs = [
    { id: "team", label: "Team", highlight: true },
    { id: "wallets", label: "Wallets" },
    { id: "quests", label: "Your Quests", count: 4 },
    { id: "leaderboard", label: "Leaderboard" },
    { id: "connections", label: "Connections" },
    { id: "settings", label: "Settings" },
  ];

  // Get connected wallet from localStorage or user object
  const connectedWalletAddress = localStorage.getItem('burnz_wallet_address') || user?.wallet_address || null;
  const connectedWalletName = localStorage.getItem('burnz_wallet') || 'Cardano Wallet';
  
  const connectedWallets = connectedWalletAddress ? [
    { 
      id: 1, 
      name: connectedWalletName.charAt(0).toUpperCase() + connectedWalletName.slice(1), 
      address: connectedWalletAddress,
      icon: connectedWalletName === 'nami' ? '🦊' : connectedWalletName === 'eternl' ? '🔷' : connectedWalletName === 'flint' ? '🔥' : '💳',
      isDefault: true 
    },
  ] : [];

  const recentActivity = [
    { id: 1, type: "Quest Completed", date: "Today", status: "completed", score: "+500 XP" },
    { id: 2, type: "NFT Dumped", date: "Yesterday", status: "completed", score: "+100 XP" },
    { id: 3, type: "Pool Joined", date: "2 days ago", status: "completed", score: "+50 XP" },
  ];

  const challenges = [
    { id: 1, title: "Play 3 times and achieve at least 700 points each time", endsIn: "23H 40M 24S", tokens: 24, diamonds: 44, image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&h=200&fit=crop" },
    { id: 2, title: "Play 3 times and achieve at least 700 points each time", endsIn: "23H 40M 24S", tokens: 24, diamonds: 44, image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=200&fit=crop" },
    { id: 3, title: "Play 3 times and achieve at least 700 points each time", endsIn: "23H 40M 24S", tokens: 24, diamonds: 44, image: "https://images.unsplash.com/photo-1493711662062-fa541f7f09ef?w=400&h=200&fit=crop" },
  ];

  // Format wallet address for display
  const formatAddress = (addr) => {
    if (!addr) return 'Not connected';
    if (addr.length > 20) {
      return `${addr.slice(0, 12)}...${addr.slice(-8)}`;
    }
    return addr;
  };

  return (
    <div className="flex gap-6">
      {/* Buy Modal */}
      <Dialog open={showBuyModal} onOpenChange={setShowBuyModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Buy BURNZ Tokens</DialogTitle>
            <DialogDescription className="text-white/50">Purchase BURNZ tokens with ADA</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-xl">
              <p className="text-orange-400 text-sm">BURNZ tokens can be purchased on UEX.us exchange</p>
            </div>
            <a href={UEX_REFERRAL} target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12">
                <ExternalLink className="w-4 h-4 mr-2" />Go to UEX.us Exchange
              </Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Swap Modal */}
      <Dialog open={showSwapModal} onOpenChange={setShowSwapModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Swap Tokens</DialogTitle>
            <DialogDescription className="text-white/50">Swap between ADA and BURNZ</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
              <p className="text-purple-400 text-sm">Token swaps are available on UEX.us</p>
            </div>
            <Link to="/app/exchange">
              <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white h-12">
                <Repeat className="w-4 h-4 mr-2" />Go to Exchange Page
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Convert XP to BURNZ</DialogTitle>
            <DialogDescription className="text-white/50">Convert your earned XP into BURNZ tokens</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#0D0D0D] rounded-xl">
              <div className="flex justify-between mb-2">
                <span className="text-white/50">Your XP</span>
                <span className="text-cyan-400 font-bold">{user?.xp || DEMO.user.xp} XP</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-white/50">Conversion Rate</span>
                <span className="text-white">10 XP = 1 BURNZ</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">You will receive</span>
                <span className="text-orange-400 font-bold">{Math.floor((user?.xp || DEMO.user.xp) / 10)} BURNZ</span>
              </div>
            </div>
            <Button onClick={handleClaim} disabled={claiming} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12">
              {claiming ? "Converting..." : "Convert XP to BURNZ"}
            </Button>
            <p className="text-white/30 text-xs text-center">2% burn fee applies to all conversions</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Twitter Connection Modal */}
      <Dialog open={showTwitterModal} onOpenChange={setShowTwitterModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-[#1DA1F2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Connect Twitter/X
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Link your Twitter account to verify social quests
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#1DA1F2]/10 border border-[#1DA1F2]/20 rounded-xl">
              <p className="text-[#1DA1F2] text-sm mb-2 font-medium">Manual Verification Mode</p>
              <p className="text-white/50 text-sm">
                By clicking "Confirm Connection", you confirm that you have a Twitter/X account and will use it to complete social quests. Quest completions will be manually verified.
              </p>
            </div>
            <Button 
              onClick={handleConnectTwitter}
              disabled={connectingTwitter}
              data-testid="confirm-twitter-connection-btn"
              className="w-full bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white h-12"
            >
              {connectingTwitter ? "Connecting..." : "Confirm Connection"}
            </Button>
            <p className="text-white/30 text-xs text-center">
              This is a manual verification system. We don't require OAuth access.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discord Connection Modal */}
      <Dialog open={showDiscordModal} onOpenChange={setShowDiscordModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Connect Discord
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Link your Discord account to verify community quests
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#5865F2]/10 border border-[#5865F2]/20 rounded-xl">
              <p className="text-[#5865F2] text-sm mb-2 font-medium">Manual Verification Mode</p>
              <p className="text-white/50 text-sm">
                By clicking "Confirm Connection", you confirm that you have a Discord account and will use it to join required servers. Quest completions will be manually verified.
              </p>
            </div>
            <Button 
              onClick={handleConnectDiscord}
              disabled={connectingDiscord}
              data-testid="confirm-discord-connection-btn"
              className="w-full bg-[#5865F2] hover:bg-[#4752c4] text-white h-12"
            >
              {connectingDiscord ? "Connecting..." : "Confirm Connection"}
            </Button>
            <p className="text-white/30 text-xs text-center">
              This is a manual verification system. We don't require OAuth access.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="flex-1 space-y-6">
        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-white/10 pb-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
                activeTab === tab.id 
                  ? "text-white bg-white/5 border-b-2 border-orange-500" 
                  : tab.highlight 
                    ? "text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                    : "text-white/50 hover:text-white hover:bg-white/5"
              }`}
            >
              {tab.label}{tab.count && <span className="ml-1 text-white/30">/{tab.count}</span>}
              {tab.highlight && activeTab !== tab.id && <span className="ml-1.5 text-[9px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">NEW</span>}
            </button>
          ))}
        </div>

        {/* Team Tab Content */}
        {activeTab === "team" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Your Team</h2>
                <p className="text-white/50 text-sm">Manage your team membership and view history</p>
              </div>
            </div>

            {/* Current Team Card */}
            {currentTeam ? (
              <Card className="bg-[#1A1A1A] border-white/10 overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${currentTeam.theme || 'from-orange-500 to-red-600'}`} />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${currentTeam.theme || 'from-orange-500 to-red-600'} flex items-center justify-center text-3xl`}>
                        {currentTeam.emoji || '🔥'}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{currentTeam.name}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                            <Trophy className="w-3 h-3 mr-1" />Rank #{currentTeam.rank}
                          </Badge>
                          <span className="text-white/40 text-sm">
                            {(currentTeam.totalXp / 1000000).toFixed(1)}M Total XP
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setShowTeamSwitchModal(true)}
                      variant="outline"
                      className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      data-testid="switch-team-btn"
                    >
                      <Users className="w-4 h-4 mr-2" />Switch Team
                    </Button>
                  </div>

                  {/* User Stats in Team */}
                  <div className="grid grid-cols-4 gap-4 mt-6">
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-orange-400">{currentTeam.myContribution.toLocaleString()}</p>
                      <p className="text-white/40 text-xs">XP Contributed</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-red-400">{currentTeam.myBurnContribution}</p>
                      <p className="text-white/40 text-xs">BURNZ Burned</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-purple-400">
                        {currentTeam.teamBurnTotal > 0 ? ((currentTeam.myBurnContribution / currentTeam.teamBurnTotal) * 100).toFixed(1) : '0'}%
                      </p>
                      <p className="text-white/40 text-xs">Burn Contribution</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-cyan-400">{daysSinceJoin}</p>
                      <p className="text-white/40 text-xs">Days in Team</p>
                    </div>
                  </div>

                  {/* Team Badges */}
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-white/50 text-sm">Team Badges:</span>
                    <Badge className="bg-orange-500/20 text-orange-400 border-0">🔥 First Contribution</Badge>
                    <Badge className="bg-green-500/20 text-green-400 border-0">✅ Weekly Active</Badge>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardContent className="p-8 text-center">
                  <Shield className="w-16 h-16 mx-auto mb-4 text-white/20" />
                  <h3 className="text-white font-bold text-lg mb-2">No Team Yet</h3>
                  <p className="text-white/50 mb-4">Join a team to compete on leaderboards and earn together!</p>
                  <Button onClick={() => setShowTeamSwitchModal(true)} className="bg-purple-500 hover:bg-purple-600 text-white">
                    <Users className="w-4 h-4 mr-2" />Join a Team
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Switching Rules Info - User-Facing Copy */}
            <Card className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/20">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-bold mb-2">Team Membership Rules</h4>
                    <p className="text-white/60 text-sm mb-4">
                      You can only belong to <span className="text-purple-400 font-semibold">one active team</span> at a time. 
                      Your XP and burns contribute exclusively to your current team.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <p className="text-white/80 font-medium">⏱️ Cooldowns</p>
                        <p className="text-white/50">• <span className="text-white">7 days</span> after joining</p>
                        <p className="text-white/50">• <span className="text-white">14 days</span> after switching</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-white/80 font-medium">🔥 Switch Cost</p>
                        <p className="text-white/50">• First switch: <span className="text-green-400">FREE</span></p>
                        <p className="text-white/50">• After: <span className="text-orange-400">250-1,000 BURNZ</span></p>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-yellow-400 text-xs flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>When you switch:</strong> Your personal XP stays with you forever, but your 
                          team contributions (XP & burns) reset to zero. Your history and badges from past teams are preserved.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team History - Enhanced Visual Indicators */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-white/50" />
                    Your Team Journey
                  </h3>
                  <p className="text-white/40 text-xs mt-1">Past teams are read-only • Badges and achievements preserved</p>
                </div>
                <div className="flex items-center gap-3 text-sm bg-white/5 px-3 py-1.5 rounded-lg">
                  <span className="text-white/70">{totalTeamsJoined} Teams</span>
                  <span className="text-white/30">•</span>
                  <span className="text-white/70">{totalDaysInTeams} Days</span>
                  <span className="text-white/30">•</span>
                  <span className="text-orange-400 font-medium">{totalXpContributed.toLocaleString()} XP</span>
                  <span className="text-white/30">•</span>
                  <span className="text-red-400 font-medium">{totalBurnzBurned} BURNZ</span>
                </div>
              </div>

              <div className="space-y-3">
                {teamHistory.map((team, i) => (
                  <Card 
                    key={team.id + i} 
                    className={`border transition-all ${
                      team.isCurrent 
                        ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/5 border-green-500/30 ring-2 ring-green-500/20' 
                        : 'bg-[#1A1A1A] border-white/10 opacity-75 hover:opacity-100'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                            team.isCurrent 
                              ? 'bg-green-500/20 ring-2 ring-green-500/30' 
                              : 'bg-white/10'
                          }`}>
                            {team.emoji}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${team.isCurrent ? 'text-white' : 'text-white/70'}`}>{team.name}</p>
                              {team.isCurrent ? (
                                <Badge className="bg-green-500 text-white border-0 text-xs px-2 py-0.5 font-bold">
                                  ⚡ ACTIVE
                                </Badge>
                              ) : (
                                <Badge className="bg-white/10 text-white/50 border-0 text-xs px-2 py-0.5">
                                  📜 History
                                </Badge>
                              )}
                            </div>
                            <p className="text-white/40 text-xs mt-0.5">
                              {team.isCurrent ? (
                                <>Joined {new Date(team.joinedAt).toLocaleDateString()} • {team.daysInTeam || daysSinceJoin} days active</>
                              ) : (
                                <>{new Date(team.joinedAt).toLocaleDateString()} → {new Date(team.leftAt).toLocaleDateString()} • {team.daysInTeam} days</>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className={`font-medium ${team.isCurrent ? 'text-orange-400' : 'text-orange-400/60'}`}>
                              {team.xpContributed.toLocaleString()} XP
                            </p>
                            <p className="text-white/30 text-xs">{team.isCurrent ? 'contributing' : 'contributed'}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-medium ${team.isCurrent ? 'text-red-400' : 'text-red-400/60'}`}>
                              {team.burnzBurned} BURNZ
                            </p>
                            <p className="text-white/30 text-xs">{team.isCurrent ? 'burning' : 'burned'}</p>
                          </div>
                          {team.achievementsEarned?.length > 0 && (
                            <div className="flex gap-1 pl-4 border-l border-white/10">
                              {team.achievementsEarned.map((a, j) => (
                                <span key={j} className="text-lg" title={a}>
                                  {a === 'first_contribution' ? '🔥' : a === 'weekly_active' ? '✅' : a === 'team_milestone' ? '🏆' : '⭐'}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {!team.isCurrent && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          <p className="text-white/30 text-xs italic">
                            Read-only • XP and burns no longer contribute to this team
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Team Switch Modal */}
        <Dialog open={showTeamSwitchModal} onOpenChange={setShowTeamSwitchModal}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Switch Team
              </DialogTitle>
              <DialogDescription className="text-white/50">
                Select a new team to join. Your personal XP stays, but team contributions reset.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Status & Requirements */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-3 rounded-lg border ${canSwitch ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
                  <p className="text-xs text-white/50 mb-1">Switch Status</p>
                  {canSwitch ? (
                    <p className="text-green-400 font-medium flex items-center gap-1">
                      <Check className="w-4 h-4" />Ready to Switch
                    </p>
                  ) : cooldownRemaining > 0 ? (
                    <p className="text-red-400 font-medium flex items-center gap-1">
                      <Clock className="w-4 h-4" />{cooldownRemaining} days cooldown remaining
                    </p>
                  ) : (
                    <p className="text-red-400 font-medium flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />Active campaign in progress
                    </p>
                  )}
                </div>
                <div className="p-3 rounded-lg border border-orange-500/30 bg-orange-500/10">
                  <p className="text-xs text-white/50 mb-1">Switch Cost</p>
                  {getSwitchCost() === 0 ? (
                    <p className="text-green-400 font-medium">FREE (First Switch)</p>
                  ) : (
                    <p className="text-orange-400 font-medium flex items-center gap-1">
                      <Flame className="w-4 h-4" />{getSwitchCost()} BURNZ (Burned)
                    </p>
                  )}
                </div>
              </div>

              {/* Warning Banner */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-yellow-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>Switching resets your team XP contribution and burn contribution to zero!</span>
                </p>
              </div>

              {/* Available Teams */}
              <div>
                <p className="text-white/70 text-sm font-medium mb-3">Select New Team</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableTeams.filter(t => t.id !== currentTeam?.id).map((team) => (
                    <div 
                      key={team.id}
                      onClick={() => canSwitch && setSelectedNewTeam(team.id)}
                      className={`p-4 rounded-lg border transition-all cursor-pointer ${
                        selectedNewTeam === team.id 
                          ? 'border-purple-500 bg-purple-500/20' 
                          : canSwitch 
                            ? 'border-white/10 bg-white/5 hover:border-white/20' 
                            : 'border-white/5 bg-white/5 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${team.theme} flex items-center justify-center text-2xl`}>
                            {team.emoji}
                          </div>
                          <div>
                            <p className="text-white font-medium">{team.name}</p>
                            <div className="flex items-center gap-2 text-xs text-white/40">
                              <span>Rank #{team.rank}</span>
                              <span>•</span>
                              <span>{team.activeMemberCount.toLocaleString()} members</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-400 font-medium">{(team.totalXp / 1000000).toFixed(1)}M XP</p>
                          <p className="text-green-400 text-xs">{team.momentum} momentum</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button 
                  variant="outline" 
                  className="flex-1 border-white/10 text-white/50"
                  onClick={() => { setShowTeamSwitchModal(false); setSelectedNewTeam(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                  disabled={!canSwitch || !selectedNewTeam || switchingTeam}
                  onClick={handleTeamSwitch}
                  data-testid="confirm-team-switch-btn"
                >
                  {switchingTeam ? (
                    <>Switching...</>
                  ) : (
                    <>
                      <Users className="w-4 h-4 mr-2" />
                      {getSwitchCost() > 0 ? `Switch & Burn ${getSwitchCost()} BURNZ` : 'Switch Team'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Wallets Tab Content */}
        {activeTab === "wallets" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Wallets</h2>

            {/* Total Balance */}
            <div className="space-y-4">
              <h3 className="text-white/70 text-sm font-medium">Total balance</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-2">Experience (XP)</p>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                      <Gem className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xl font-bold text-cyan-400">{user?.xp || DEMO.user.xp}</span>
                  </div>
                </div>
                <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-2">BURNZ Tokens</p>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <Flame className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xl font-bold text-orange-400">{user?.burnz_tokens || DEMO.user.burnz_tokens}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-4">
                <Button onClick={() => setShowBuyModal(true)} variant="outline" className="bg-[#1A1A1A] border-white/10 text-white hover:bg-white/10 h-12">
                  <Wallet className="w-4 h-4 mr-2" />Buy
                </Button>
                <Button onClick={() => setShowSwapModal(true)} variant="outline" className="bg-[#1A1A1A] border-white/10 text-white hover:bg-white/10 h-12">
                  <Repeat className="w-4 h-4 mr-2" />Swap
                </Button>
                <Button onClick={() => setShowConvertModal(true)} variant="outline" className="bg-[#1A1A1A] border-white/10 text-white hover:bg-white/10 h-12">
                  <ArrowUpRight className="w-4 h-4 mr-2" />Convert
                </Button>
              </div>
            </div>

            {/* Connected Wallets */}
            <div className="space-y-4">
              <h3 className="text-white/70 text-sm font-medium">Connected wallets</h3>
              <div className="space-y-3">
                {connectedWallets.length > 0 ? (
                  connectedWallets.map(wallet => (
                    <div key={wallet.id} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xl">
                            {wallet.icon}
                          </div>
                          <div>
                            <span className="text-white font-medium">{wallet.name}</span>
                            {wallet.isDefault && <Badge className="ml-2 bg-green-500/20 text-green-400 border-0 text-xs">Default</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 p-2 bg-black/30 rounded-lg">
                        <code className="text-xs text-white/70 flex-1 font-mono">{formatAddress(wallet.address)}</code>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-white/50 hover:text-white"
                          onClick={() => {
                            navigator.clipboard.writeText(wallet.address);
                            alert('Address copied!');
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 text-center">
                    <Wallet className="w-12 h-12 mx-auto mb-3 text-white/20" />
                    <p className="text-white/50 mb-4">No wallet connected</p>
                    <Button onClick={() => setWalletModalOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
                      Connect Wallet
                    </Button>
                  </div>
                )}
              </div>
              <Button onClick={() => setWalletModalOpen(true)} variant="outline" className="w-full bg-[#1A1A1A] border-white/10 text-white/50 hover:bg-white/10 h-12">
                <Plus className="w-4 h-4 mr-2" />Add new wallet
              </Button>
            </div>

            {/* Recent Activity */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white/70 text-sm font-medium">Recent activity</h3>
                <div className="flex items-center gap-2 bg-[#1A1A1A] rounded-lg p-1">
                  {["all", "deposit", "withdraw"].map(filter => (
                    <button
                      key={filter}
                      onClick={() => setActivityFilter(filter)}
                      className={`px-3 py-1 text-xs rounded-md transition-all capitalize ${
                        activityFilter === filter
                          ? "bg-white/10 text-white"
                          : "text-white/50 hover:text-white"
                      }`}
                    >
                      {filter === "all" ? "All" : filter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Activity Table */}
              <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/30 text-xs font-normal p-4">Name <ChevronDown className="w-3 h-3 inline ml-1" /></th>
                      <th className="text-left text-white/30 text-xs font-normal p-4">Date <ChevronDown className="w-3 h-3 inline ml-1" /></th>
                      <th className="text-left text-white/30 text-xs font-normal p-4">Status <ChevronDown className="w-3 h-3 inline ml-1" /></th>
                      <th className="text-right text-white/30 text-xs font-normal p-4">Score <ChevronDown className="w-3 h-3 inline ml-1" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.filter(a => activityFilter === "all" || a.type.toLowerCase() === activityFilter).map(activity => (
                      <tr key={activity.id} className="border-b border-white/5 last:border-0">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activity.type === "Deposit" ? "bg-orange-500/20" : "bg-cyan-500/20"}`}>
                              <Flame className={`w-4 h-4 ${activity.type === "Deposit" ? "text-orange-400" : "text-cyan-400"}`} />
                            </div>
                            <span className="text-white font-medium">{activity.type}</span>
                          </div>
                        </td>
                        <td className="p-4 text-white/50">{activity.date}</td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 text-xs uppercase ${
                            activity.status === "completed" ? "text-green-400" :
                            activity.status === "pending" ? "text-blue-400" : "text-red-400"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              activity.status === "completed" ? "bg-green-400" :
                              activity.status === "pending" ? "bg-blue-400" : "bg-red-400"
                            }`} />
                            {activity.status}
                          </span>
                        </td>
                        <td className="p-4 text-right text-white">{activity.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Settings</h2>
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Email Notifications</p>
                    <p className="text-white/50 text-sm">Receive updates about quests and rewards</p>
                  </div>
                  <Button variant="outline" size="sm" className="bg-orange-500/20 border-orange-500/30 text-orange-400">Enabled</Button>
                </div>
                <Separator className="bg-white/10" />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Two-Factor Authentication</p>
                    <p className="text-white/50 text-sm">Add an extra layer of security</p>
                  </div>
                  <Button variant="outline" size="sm" className="bg-white/5 border-white/20 text-white">Setup</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quests Tab */}
        {activeTab === "quests" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Your Quests</h2>
            <div className="grid gap-4">
              {DEMO.quests.slice(0, 4).map(quest => (
                <Card key={quest.id} className="bg-[#1A1A1A] border-white/10">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                        <Target className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{quest.title}</p>
                        <p className="text-white/50 text-sm">{quest.xp_reward} XP • {quest.difficulty}</p>
                      </div>
                    </div>
                    <Badge className="bg-orange-500/20 text-orange-400 border-0">{quest.status}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Leaderboard</h2>
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardContent className="p-4 space-y-3">
                {DEMO.leaderboard.map((player, i) => (
                  <div key={i} className={`flex items-center gap-4 p-3 rounded-xl ${i < 3 ? "bg-orange-500/10" : "bg-white/5"}`}>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${i === 0 ? "bg-yellow-500 text-black" : i === 1 ? "bg-gray-400 text-black" : i === 2 ? "bg-orange-700 text-white" : "bg-white/10 text-white"}`}>{player.rank}</span>
                    <span className="text-2xl">{player.avatar}</span>
                    <div className="flex-1">
                      <p className="text-white font-medium">{player.name}</p>
                      <p className="text-white/50 text-xs">{player.level}</p>
                    </div>
                    <span className="text-orange-400 font-bold">{player.xp.toLocaleString()} XP</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Connections Tab */}
        {activeTab === "connections" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Connected Accounts</h2>
              <p className="text-white/50 text-sm">Link your social accounts to participate in quests and verify completions</p>
            </div>

            {/* Why Connect Banner */}
            <Card className="bg-purple-500/10 border-purple-500/20">
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-purple-400 font-semibold mb-1">Why Connect Your Accounts?</h3>
                  <p className="text-white/50 text-sm">
                    To earn XP from social quests (retweets, follows, comments), we need to verify your actions. 
                    Connect your Twitter/X and Discord to automatically verify quest completions and earn rewards.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Twitter Connection */}
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#1DA1F2]/20 flex items-center justify-center">
                      <svg className="w-7 h-7 text-[#1DA1F2]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Twitter / X</h3>
                      <p className="text-white/50 text-sm">Required for social quests: retweets, follows, quote tweets</p>
                    </div>
                  </div>
                  {twitterConnected ? (
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-green-400 text-sm font-medium flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />Connected
                        </p>
                        <p className="text-white/30 text-xs">{twitterHandle || "@username"}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        onClick={handleDisconnectTwitter}
                        data-testid="disconnect-twitter-btn"
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white"
                      onClick={() => setShowTwitterModal(true)}
                      data-testid="connect-twitter-btn"
                    >
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      Connect Twitter
                    </Button>
                  )}
                </div>
                {!twitterConnected && (
                  <div className="mt-4 p-3 bg-white/5 rounded-lg">
                    <p className="text-white/40 text-xs">
                      Manual verification mode: Confirm your Twitter connection to participate in social quests.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Discord Connection */}
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#5865F2]/20 flex items-center justify-center">
                      <svg className="w-7 h-7 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Discord</h3>
                      <p className="text-white/50 text-sm">Required for community quests: server joins, role verification</p>
                    </div>
                  </div>
                  {discordConnected ? (
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-green-400 text-sm font-medium flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />Connected
                        </p>
                        <p className="text-white/30 text-xs">{discordHandle || "User#1234"}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        onClick={handleDisconnectDiscord}
                        data-testid="disconnect-discord-btn"
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      className="bg-[#5865F2] hover:bg-[#4752c4] text-white"
                      onClick={() => setShowDiscordModal(true)}
                      data-testid="connect-discord-btn"
                    >
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                      </svg>
                      Connect Discord
                    </Button>
                  )}
                </div>
                {!discordConnected && (
                  <div className="mt-4 p-3 bg-white/5 rounded-lg">
                    <p className="text-white/40 text-xs">
                      Manual verification mode: Confirm your Discord connection to participate in community quests.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Join Discord CTA */}
            <Card className="bg-gradient-to-r from-[#5865F2]/20 to-purple-500/20 border-[#5865F2]/30">
              <CardContent className="p-6 text-center">
                <h3 className="text-white font-bold text-lg mb-2">Join the BURNZ Union Discord</h3>
                <p className="text-white/50 text-sm mb-4">
                  Connect with fellow holders, get quest updates, and participate in exclusive events.
                </p>
                <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#5865F2] hover:bg-[#4752c4] text-white">
                    <MessageCircle className="w-4 h-4 mr-2" />Join Our Discord Server
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* UEX Sign Up */}
            <Card className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-cyan-500/30">
              <CardContent className="p-6 text-center">
                <h3 className="text-white font-bold text-lg mb-2">Trade on UEX.us</h3>
                <p className="text-white/50 text-sm mb-4">
                  Sign up on UEX.us to swap your BURNZ tokens and trade crypto with low fees.
                </p>
                <a href={UEX_REFERRAL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    <ExternalLink className="w-4 h-4 mr-2" />Sign Up on UEX.us
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Quest Verification Info */}
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />How Quest Verification Works
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: 1, title: "Connect Accounts", desc: "Link your Twitter and Discord accounts above", icon: Link },
                    { step: 2, title: "Complete Quest Tasks", desc: "Follow, retweet, join servers as required", icon: Target },
                    { step: 3, title: "Automatic Verification", desc: "We verify your actions through APIs", icon: CheckCircle2 },
                    { step: 4, title: "Earn XP & BURNZ", desc: "Rewards are added once verified", icon: Sparkles },
                  ].map(item => (
                    <div key={item.step} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-400 font-bold text-sm">{item.step}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{item.title}</p>
                        <p className="text-white/40 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Right Sidebar - Player Card */}
      <div className="w-72 flex-shrink-0 space-y-4">
        {/* Player Info Card */}
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardContent className="p-4 text-center">
            <div className="relative inline-block mb-3">
              <Avatar className="w-20 h-20 border-2 border-purple-500">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-purple-500 text-2xl">P</AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold border-2 border-[#1A1A1A]">
                {user?.level || 2}
              </div>
            </div>
            <h3 className="text-white font-bold text-lg">{user?.username || "Ocratz OB"}</h3>
            <p className="text-white/30 text-xs font-mono mb-4">fb213607-b08f-44f9</p>

            {/* XP Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <Badge className="bg-orange-500 text-white text-xs border-0">{user?.level || 2}</Badge>
                <Badge className="bg-purple-500 text-white text-xs border-0">{(user?.level || 2) + 1}</Badge>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-500 to-purple-500 rounded-full transition-all" style={{ width: `${user?.xp_progress || 20}%` }} />
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-white/50">Current: <span className="text-white">{user?.xp || 40}XP</span></span>
                <span className="text-white/50">Left to earn: <span className="text-white">{(user?.next_level_xp || 200) - (user?.xp || 40)}XP</span></span>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white/5 rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center mx-auto mb-2">
                  <Flame className="w-4 h-4 text-orange-400" />
                </div>
                <p className="text-white font-bold">24</p>
                <p className="text-white/30 text-xs">Token</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center mx-auto mb-2">
                  <Target className="w-4 h-4 text-purple-400" />
                </div>
                <p className="text-white font-bold">4<span className="text-white/30">/204</span></p>
                <p className="text-white/30 text-xs">Quest</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center mx-auto mb-2">
                  <Award className="w-4 h-4 text-cyan-400" />
                </div>
                <p className="text-white font-bold">2<span className="text-white/30">/45</span></p>
                <p className="text-white/30 text-xs">Achievement</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hurry Up Challenges */}
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-white/50 text-xs tracking-widest">
            <span>H U R R Y</span>
            <span>U P</span>
            <Timer className="w-4 h-4 text-orange-400" />
            <span>F I N I S H</span>
            <span>I T</span>
          </div>

          {challenges.map(challenge => (
            <Card key={challenge.id} className="bg-[#1A1A1A] border-white/10 overflow-hidden">
              <div className="relative h-24">
                <img src={challenge.image} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent" />
                <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded text-xs text-white">
                  Ends in <span className="text-orange-400 font-bold">{challenge.endsIn}</span>
                </div>
              </div>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center">
                      <Flame className="w-2 h-2 text-white" />
                    </div>
                    <span className="text-orange-400 text-sm font-bold">{challenge.tokens}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center">
                      <Gem className="w-2 h-2 text-white" />
                    </div>
                    <span className="text-cyan-400 text-sm font-bold">{challenge.diamonds}</span>
                  </div>
                </div>
                <p className="text-white/70 text-xs leading-relaxed">{challenge.title}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export { ProfilePage };
